/**
 * Team Redux Operations (Thunks)
 * Async operations for team data
 */

import * as actions from 'src/routes/team/team.actions';
import {
    getTeamMembersByAgency,
    getInvitationsByAgency,
    removeAgencyMembership,
    transferAgencyOwnership,
    updateUserRole,
} from 'src/services/requests/users-and-permissions/users/users';
import { sendInvitations, updateInvitation, approveRejectInvitation, revokeInvitation, revokeLytInvitation, resendInvitation, resendLytInvitation } from 'src/services/requests/users-and-permissions/invitations';
import { isLytRole } from 'src/constants/roles.constants';
import { showErrorToast, showSuccessToast, showWarningToast } from 'src/components/toast';
import {
    processInvitationSendError,
    processInvitationSendResponse,
    createInvitationSendOperationEvent,
    getInvitationSendSafeRequestData,
} from 'src/utils/invite-user-response.utils';
import { userEffectiveAgencyIdSelector } from 'src/store/user/user.selectors';
import { normalizeTeamMembers, normalizeInvitations } from 'src/data/Team/team-normalizers';
import { LOG_EVENT_TYPE, createLogEvent } from 'src/logger/log-event';
import { LOG_LEVEL, logger } from 'src/logger/logger';
import { createTransactionId } from 'src/logger/transaction-id';

const CODE_LOCATION = 'src/routes/team/team.operations.js';

const createTeamListOperationEvent = ({
    methodName,
    transactionId,
    level,
    message,
    outcome,
    contentType,
    contentAvailable,
    resultCount,
    failureReason,
}) => createLogEvent({
    eventType: LOG_EVENT_TYPE.OPERATION,
    codeLocation: CODE_LOCATION,
    methodName,
    level,
    message,
    outcome,
    transactionId,
    attributes: {
        frontend_data: {
            content_type: contentType,
            content_available: contentAvailable,
            ...(typeof resultCount === 'number'
                ? { normalized_result_count: resultCount }
                : {}),
            ...(!contentAvailable
                ? {
                    retry_available: true,
                    reason: failureReason,
                }
                : {}),
        },
    },
});

/** Default page size for the team-members endpoint */
const DEFAULT_TEAM_MEMBERS_LIMIT = 10;

/**
 * Fetch team members (users + invites) with server-side pagination, sorting, and filtering.
 *
 * All filtering / sorting is done by the backend.
 * The response contains a mixed list where each item has an `is_prospect` boolean.
 *
 * @param {Object} [params] - Query parameters (merged with stored params in Redux)
 * @param {string}  [params.searchTerm]  - Free-text search across name / email
 * @param {number}  [params.page=1]      - 1-based page number
 * @param {number}  [params.limit]       - Rows per page (falls back to stored / default)
 * @param {string}  [params.sortBy]      - 'name' | 'role' | 'last_active'
 * @param {string}  [params.sortOrder]   - 'asc' | 'desc'
 * @param {string}  [params.roleFilter]  - Exact role value, e.g. 'traffic_viewer'
 * @returns {Function} Redux thunk
 */
export const getTeamMembersOperation = (params = {}) => async (dispatch, getState) => {
    // Merge incoming params over whatever is already stored in Redux
    const storedParams = getState().team?.members?.params || {};
    const merged = { ...storedParams, ...params };

    const {
        searchTerm,
        page = 1,
        limit = DEFAULT_TEAM_MEMBERS_LIMIT,
        sortBy,
        sortOrder,
        roleFilter,
    } = merged;

    // Persist the resolved params so the UI can read them back
    dispatch(actions.setTeamMembersParams({ searchTerm, page, limit, sortBy, sortOrder, roleFilter }));
    dispatch(actions.getTeamMembersRequest());

    // Read the effective agency for the current user
    const agencyId = userEffectiveAgencyIdSelector(getState());

    // Compute zero-based offset from 1-based page number
    const offset = (page - 1) * limit;
    const transactionId = createTransactionId();

    let response;
    try {
        response = await getTeamMembersByAgency({
            agencyId: agencyId || undefined,
            searchTerm: searchTerm || undefined,
            offset,
            limit,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            roleFilter: roleFilter || undefined,
            transactionId,
        });
    } catch (error) {
        logger.event(createTeamListOperationEvent({
            methodName: 'getTeamMembersOperation',
            transactionId,
            level: LOG_LEVEL.ERROR,
            message: 'Team members unavailable',
            outcome: 'failure',
            contentType: 'team_members',
            contentAvailable: false,
            failureReason: 'service_request_failed',
        }));
        dispatch(actions.getTeamMembersError(error));
        dispatch(showErrorToast(
            'Failed to load team members',
            'Something went wrong while loading your team. Please try again.',
        ));
        throw error;
    }

    // axios wraps in response.data → { pagination, data }
    const responseData = response?.data;
    const rawData = responseData?.data || [];
    const pagination = responseData?.pagination || { offset: 0, limit, count: 0 };

    let data;
    try {
        data = normalizeTeamMembers(rawData);
    } catch (error) {
        logger.event(createTeamListOperationEvent({
            methodName: 'getTeamMembersOperation',
            transactionId,
            level: LOG_LEVEL.ERROR,
            message: 'Team members unavailable',
            outcome: 'failure',
            contentType: 'team_members',
            contentAvailable: false,
            failureReason: 'response_processing_failed',
        }));
        dispatch(actions.getTeamMembersError(error));
        dispatch(showErrorToast(
            'Failed to load team members',
            'Something went wrong while loading your team. Please try again.',
        ));
        throw error;
    }

    logger.event(createTeamListOperationEvent({
        methodName: 'getTeamMembersOperation',
        transactionId,
        level: LOG_LEVEL.INFO,
        message: 'Team members available',
        outcome: 'success',
        contentType: 'team_members',
        contentAvailable: true,
        resultCount: data.length,
    }));
    dispatch(actions.getTeamMembersSuccess({ data, pagination }));
    return { data, pagination };
};

/**
 * Reset team members state (on unmount or agency change)
 * @returns {Function} Redux thunk
 */
export const resetTeamMembersOperation = () => (dispatch) => {
    dispatch(actions.resetTeamMembers());
};

/** Default page size for the invitations endpoint */
const DEFAULT_REQUESTS_LIMIT = 10;

/**
 * Fetch pending requests (invitations) with server-side pagination, sorting, and filtering.
 *
 * @param {Object} [params] - Query parameters (merged with stored params in Redux)
 * @param {string}  [params.searchTerm]  - Free-text search across name / email
 * @param {number}  [params.page=1]      - 1-based page number
 * @param {number}  [params.limit]       - Rows per page (falls back to stored / default)
 * @param {string}  [params.sortBy]      - 'name' | 'role' | 'last_active'
 * @param {string}  [params.sortOrder]   - 'asc' | 'desc'
 * @param {string}  [params.roleFilter]  - Exact role value, e.g. 'traffic_viewer'
 * @returns {Function} Redux thunk
 */
export const getTeamRequestsOperation = (params = {}) => async (dispatch, getState) => {
    const storedParams = getState().team?.requests?.params || {};
    const merged = { ...storedParams, ...params };

    const {
        searchTerm,
        page = 1,
        limit = DEFAULT_REQUESTS_LIMIT,
        sortBy,
        sortOrder,
        roleFilter,
    } = merged;

    dispatch(actions.setTeamRequestsParams({ searchTerm, page, limit, sortBy, sortOrder, roleFilter }));
    dispatch(actions.getTeamRequestsRequest());

    const agencyId = userEffectiveAgencyIdSelector(getState());

    // Compute zero-based offset from 1-based page number
    const offset = (page - 1) * limit;
    const transactionId = createTransactionId();

    let response;
    try {
        response = await getInvitationsByAgency({
            agencyId: agencyId || undefined,
            searchTerm: searchTerm || undefined,
            offset,
            limit,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            roleFilter: roleFilter || undefined,
            transactionId,
        });
    } catch (error) {
        logger.event(createTeamListOperationEvent({
            methodName: 'getTeamRequestsOperation',
            transactionId,
            level: LOG_LEVEL.ERROR,
            message: 'Team requests unavailable',
            outcome: 'failure',
            contentType: 'team_requests',
            contentAvailable: false,
            failureReason: 'service_request_failed',
        }));
        dispatch(actions.getTeamRequestsError(error));
        dispatch(showErrorToast(
            'Failed to load requests',
            'Something went wrong while loading requests. Please try again.',
        ));
        throw error;
    }

    const responseData = response?.data;
    const rawData = responseData?.data || [];
    const pagination = responseData?.pagination || { offset: 0, limit, count: 0 };

    let data;
    try {
        data = normalizeInvitations(rawData);
    } catch (error) {
        logger.event(createTeamListOperationEvent({
            methodName: 'getTeamRequestsOperation',
            transactionId,
            level: LOG_LEVEL.ERROR,
            message: 'Team requests unavailable',
            outcome: 'failure',
            contentType: 'team_requests',
            contentAvailable: false,
            failureReason: 'response_processing_failed',
        }));
        dispatch(actions.getTeamRequestsError(error));
        dispatch(showErrorToast(
            'Failed to load requests',
            'Something went wrong while loading requests. Please try again.',
        ));
        throw error;
    }

    logger.event(createTeamListOperationEvent({
        methodName: 'getTeamRequestsOperation',
        transactionId,
        level: LOG_LEVEL.INFO,
        message: 'Team requests available',
        outcome: 'success',
        contentType: 'team_requests',
        contentAvailable: true,
        resultCount: data.length,
    }));
    dispatch(actions.getTeamRequestsSuccess({ data, pagination }));
    return { data, pagination };
};

/**
 * Reset team requests state (on unmount or agency change)
 * @returns {Function} Redux thunk
 */
export const resetTeamRequestsOperation = () => (dispatch) => {
    dispatch(actions.resetTeamRequests());
};

/**
 * Send user invitations and refresh team data on success.
 *
 * The API responds with one of:
 *  - 201 success: { successfulInvitationsProcessCount, failedInvitationsProcessCount, errors }
 *  - 400 validation: { statusCode, error, message, errors }
 *  - 401/403/404/500: standard HTTP error payloads
 *
 * On success the operation dispatches a success toast and re-fetches the team
 * members list. The raw API response is always returned so the calling UI can
 * react to partial failures or validation errors.
 *
 * @param {Object} payload
 * @param {string[]} payload.recipients
 * @param {string}   payload.assigned_role
 * @param {string}   payload.agency_id
 * @param {string[]} [payload.jurisdictions]
 * @param {string}   [payload.customized_message]
 * @returns {Function} Redux thunk that resolves with the API response
 */
export const sendInvitationsOperation = (payload) => async (dispatch) => {
    const transactionId = createTransactionId();
    const requestContext = getInvitationSendSafeRequestData(payload);

    let response;
    try {
        response = await sendInvitations({
            ...payload,
            transactionId,
        });
    } catch (error) {
        const result = processInvitationSendError(error);

        if (result.toast) {
            dispatch(showErrorToast(result.toast.title, result.toast.content));
        }

        logger.event(createInvitationSendOperationEvent({
            codeLocation: CODE_LOCATION,
            methodName: 'sendInvitationsOperation',
            transactionId,
            result,
            requestContext,
        }));

        return result;
    }

    const result = processInvitationSendResponse(response);

    if (result.successCount > 0) {
        dispatch(showSuccessToast(
            `${result.successCount} user(s) invited successfully.`,
        ));
        dispatch(getTeamMembersOperation({ page: 1 }));
    }

    if (result.partialSuccess) {
        dispatch(showWarningToast(
            'Some invitations could not be sent',
            result.inlineError,
        ));
    }

    logger.event(createInvitationSendOperationEvent({
        codeLocation: CODE_LOCATION,
        methodName: 'sendInvitationsOperation',
        transactionId,
        result,
        requestContext,
    }));

    return result;
};

/**
 * Update the role on an existing invitation request.
 *
 * Calls PATCH /invitations/update with query params.
 *
 * On success the operation shows a toast and re-fetches the requests list so
 * the table reflects the updated role.
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   payload.assigned_role   - New role value
 * @param {string[]} [payload.jurisdictions] - Jurisdiction IDs (for traffic_viewer)
 * @param {string}   payload.request_type    - e.g. 'update_role'
 * @param {string}   [payload.change_reason] - Admin-provided reason for the change
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const updateInvitationOperation = (payload) => async (dispatch) => {
    try {
        const response = await updateInvitation(payload);

        dispatch(showSuccessToast('Role updated successfully.'));

        // Refresh the requests list to reflect the new status
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        const isValidationError = error?.statusCode === 400 || error?.error === 'Bad Request';

        if (isValidationError) {
            dispatch(showErrorToast(
                'Failed to update role',
                error?.message || 'Please check the role and jurisdictions and try again.',
            ));
            return { success: false, validationError: true, data: error };
        }

        dispatch(showErrorToast(
            'Failed to update role',
            'Something went wrong. Please try again.',
        ));

        return { success: false, validationError: false, data: error };
    }
};

/**
 * Resend an invitation via the agency or LYT endpoint.
 *
 * @param {Object} payload
 * @param {string} payload.invitee_email
 * @param {string} payload.created_at_ts
 * @param {string} [payload.agency_id]
 * @param {string} [payload.assigned_role]
 * @returns {Promise<Object>}
 */
const resendInvitationByPayload = async (payload) => {
    const identity = {
        invitee_email: payload.invitee_email,
        created_at_ts: payload.created_at_ts,
    };

    return isLytRole(payload.assigned_role)
        ? resendLytInvitation(identity)
        : resendInvitation({
            ...identity,
            agency_id: payload.agency_id,
        });
};

/**
 * Resend an expired invitation from the Team page.
 *
 * Agency invitations: PUT /invitations/resend?agency_id&invitee_email&created_at_ts
 * LYT invitations: PUT /invitations/lyt/resend?invitee_email&created_at_ts
 * On success, shows a toast and refreshes the team members list.
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID (agency invitations only)
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   [payload.assigned_role] - Role on the invitation (selects LYT endpoint)
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const resendTeamInviteOperation = (payload) => async (dispatch) => {
    try {
        const response = await resendInvitationByPayload(payload);

        dispatch(showSuccessToast('Invitation resent successfully.'));
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to resend invitation',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Resend an expired invitation from the Requests page.
 *
 * Agency invitations: PUT /invitations/resend?agency_id&invitee_email&created_at_ts
 * LYT invitations: PUT /invitations/lyt/resend?invitee_email&created_at_ts
 * On success, shows a toast and refreshes the requests list.
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID (agency invitations only)
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   [payload.assigned_role] - Role on the invitation (selects LYT endpoint)
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const resendRequestInviteOperation = (payload) => async (dispatch) => {
    try {
        const response = await resendInvitationByPayload(payload);

        dispatch(showSuccessToast('Invitation resent successfully.'));
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to resend invitation',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Revoke a pending invitation via the agency or LYT endpoint.
 *
 * @param {Object} payload
 * @param {string} payload.invitee_email
 * @param {string} payload.created_at_ts
 * @param {string} [payload.agency_id]
 * @param {string} [payload.assigned_role]
 * @returns {Promise<Object>}
 */
const revokeInvitationByPayload = async (payload) => {
    const identity = {
        invitee_email: payload.invitee_email,
        created_at_ts: payload.created_at_ts,
    };

    return isLytRole(payload.assigned_role)
        ? revokeLytInvitation(identity)
        : revokeInvitation({
            ...identity,
            agency_id: payload.agency_id,
        });
};

/**
 * Revoke a pending invitation from the Requests page.
 *
 * Agency invitations: DELETE /invitations?agency_id&invitee_email&created_at_ts
 * LYT invitations: DELETE /invitations/lyt?invitee_email&created_at_ts
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID (agency invitations only)
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   [payload.assigned_role] - Role on the invitation (selects LYT endpoint)
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const revokeRequestInviteOperation = (payload) => async (dispatch) => {
    try {
        const response = await revokeInvitationByPayload(payload);

        dispatch(showSuccessToast('Invitation revoked successfully.'));
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to revoke invitation',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Revoke a pending invitation from the Team page.
 *
 * Agency invitations: DELETE /invitations?agency_id&invitee_email&created_at_ts
 * LYT invitations: DELETE /invitations/lyt?invitee_email&created_at_ts
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID (agency invitations only)
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   [payload.assigned_role] - Role on the invitation (selects LYT endpoint)
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const revokeTeamInviteOperation = (payload) => async (dispatch) => {
    try {
        const response = await revokeInvitationByPayload(payload);

        dispatch(showSuccessToast('Invitation revoked successfully.'));
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to revoke invitation',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Approve or reject a pending invitation request.
 *
 * Calls PATCH /invitations/approve with query params.
 *
 * On success the operation shows a toast and re-fetches the requests list.
 *
 * @param {Object} payload
 * @param {string}  payload.agency_id       - Agency ID
 * @param {string}  payload.invitee_email   - Invitee email address
 * @param {string}  payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {boolean} payload.is_approved     - true to approve, false to reject
 * @param {string}  [payload.change_reason] - Reason for the decision
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const approveRejectInvitationOperation = (payload) => async (dispatch) => {
    const actionLabel = payload.is_approved ? 'approved' : 'rejected';

    try {
        const response = await approveRejectInvitation(payload);

        dispatch(showSuccessToast(`Request ${actionLabel} successfully.`));

        // Refresh the requests list to reflect the new status
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        const isValidationError = error?.statusCode === 400 || error?.error === 'Bad Request';

        if (isValidationError) {
            dispatch(showErrorToast(
                `Failed to ${payload.is_approved ? 'approve' : 'reject'} request`,
                error?.message || 'Please try again.',
            ));
            return { success: false, validationError: true, data: error };
        }

        dispatch(showErrorToast(
            `Failed to ${payload.is_approved ? 'approve' : 'reject'} request`,
            'Something went wrong. Please try again.',
        ));

        return { success: false, validationError: false, data: error };
    }
};

/**
 * Remove a user's membership from an agency and refresh the team list.
 *
 * Calls POST /users/remove-agency-membership with the user's email, the agency
 * ID, and the membership creation timestamp. On success a toast is shown and
 * the team members list is re-fetched so the removed user disappears.
 *
 * @param {Object} payload
 * @param {string} payload.user_email    - Email of the user to remove
 * @param {string} payload.agency_id     - Agency to remove the user from
 * @param {string} payload.created_at_ts - ISO timestamp of the membership
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
/**
 * Update an active team member's role and refresh the team list.
 *
 * @param {Object} payload
 * @param {string} payload.email - Member email address
 * @param {string} payload.role - New role value
 * @param {string[]} [payload.jurisdictions] - Jurisdiction IDs (for traffic_viewer)
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const updateUserRoleOperation = (payload) => async (dispatch) => {
    try {
        const response = await updateUserRole(payload);

        dispatch(showSuccessToast('Role updated successfully.'));
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to update role',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Transfer agency ownership to another team member and refresh the team list.
 *
 * @param {Object} payload
 * @param {string} payload.agency_id - Agency ID
 * @param {string} payload.new_owner_email - Email of the new agency owner
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const transferAgencyOwnershipOperation = (payload) => async (dispatch) => {
    try {
        const response = await transferAgencyOwnership(payload);

        dispatch(showSuccessToast('Ownership transferred successfully.'));
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to transfer ownership',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

export const removeAgencyMembershipOperation = (payload) => async (dispatch) => {
    try {
        const response = await removeAgencyMembership(payload);

        dispatch(showSuccessToast('User removed successfully.'));

        // Refresh the team members list so the removed user disappears
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to remove user',
            'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};
